import clsx from "clsx";
import {
	type ButtonHTMLAttributes,
	Children,
	createContext,
	type HTMLAttributes,
	isValidElement,
	type ReactNode,
	useContext,
	useId,
} from "react";

// ─── Types ───────────────────────────────────────────────────────────

interface TabsContextValue {
	value: string;
	onChange: (value: string) => void;
	baseId: string;
}

interface TabsProps extends Omit<HTMLAttributes<HTMLDivElement>, "onChange"> {
	value: string;
	onChange: (value: string) => void;
	children: ReactNode;
}

interface TabsListProps extends HTMLAttributes<HTMLDivElement> {
	children: ReactNode;
}

interface TabsTriggerProps
	extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "value"> {
	value: string;
	children: ReactNode;
}

interface TabsContentProps extends HTMLAttributes<HTMLDivElement> {
	value: string;
	children: ReactNode;
}

// ─── Context ─────────────────────────────────────────────────────────

const TabsContext = createContext<TabsContextValue | null>(null);

const useTabsContext = () => {
	const context = useContext(TabsContext);
	if (!context) {
		throw new Error("Tabs compound components must be used within <Tabs>");
	}
	return context;
};

// ─── Root ────────────────────────────────────────────────────────────

const TabsRoot = ({
	value,
	onChange,
	children,
	className,
	...props
}: TabsProps) => {
	const baseId = useId();

	return (
		<TabsContext.Provider value={{ value, onChange, baseId }}>
			<div className={clsx("flex flex-col", className)} {...props}>
				{children}
			</div>
		</TabsContext.Provider>
	);
};

// ─── List ────────────────────────────────────────────────────────────

const List = ({ children, className, ...props }: TabsListProps) => {
	const { value, onChange } = useTabsContext();

	const triggerValues: string[] = [];
	Children.forEach(children, (child) => {
		if (isValidElement(child) && child.type === Trigger) {
			triggerValues.push(child.props.value as string);
		}
	});

	const handleKeyDown = (e: React.KeyboardEvent) => {
		const currentIndex = triggerValues.indexOf(value);
		if (currentIndex === -1) return;

		let nextIndex: number | null = null;

		if (e.key === "ArrowRight") {
			nextIndex = (currentIndex + 1) % triggerValues.length;
		} else if (e.key === "ArrowLeft") {
			nextIndex =
				(currentIndex - 1 + triggerValues.length) % triggerValues.length;
		} else if (e.key === "Home") {
			nextIndex = 0;
		} else if (e.key === "End") {
			nextIndex = triggerValues.length - 1;
		}

		if (nextIndex !== null) {
			e.preventDefault();
			onChange(triggerValues[nextIndex]);
		}
	};

	return (
		<div
			role="tablist"
			onKeyDown={handleKeyDown}
			className={clsx(
				"inline-flex items-center gap-4 rounded-8 bg-grayscale-200 p-4",
				className,
			)}
			{...props}
		>
			{children}
		</div>
	);
};

// ─── Trigger ─────────────────────────────────────────────────────────

const Trigger = ({
	value: triggerValue,
	children,
	className,
	disabled,
	...props
}: TabsTriggerProps) => {
	const { value, onChange, baseId } = useTabsContext();
	const isActive = value === triggerValue;

	return (
		<button
			type="button"
			role="tab"
			id={`${baseId}-trigger-${triggerValue}`}
			aria-controls={`${baseId}-panel-${triggerValue}`}
			aria-selected={isActive}
			tabIndex={isActive ? 0 : -1}
			disabled={disabled}
			onClick={() => !disabled && onChange(triggerValue)}
			className={clsx(
				"rounded-6 px-12 py-6 text-sm font-medium transition-all duration-200 outline-none",
				isActive
					? "bg-grayscale-white text-grayscale-900 shadow-sm"
					: "text-grayscale-600 hover:text-grayscale-800",
				disabled
					? "cursor-not-allowed opacity-50"
					: "cursor-pointer focus-visible:ring-2 focus-visible:ring-primary-400",
				className,
			)}
			{...props}
		>
			{children}
		</button>
	);
};

// ─── Content ─────────────────────────────────────────────────────────

const Content = ({
	value: contentValue,
	children,
	className,
	...props
}: TabsContentProps) => {
	const { value, baseId } = useTabsContext();

	if (value !== contentValue) return null;

	return (
		<div
			role="tabpanel"
			id={`${baseId}-panel-${contentValue}`}
			aria-labelledby={`${baseId}-trigger-${contentValue}`}
			tabIndex={0}
			className={clsx(
				"mt-8 outline-none focus-visible:ring-2 focus-visible:ring-primary-400 rounded-8",
				className,
			)}
			{...props}
		>
			{children}
		</div>
	);
};

// ─── Export ──────────────────────────────────────────────────────────

const Tabs = Object.assign(TabsRoot, {
	List,
	Trigger,
	Content,
});

export default Tabs;
